import React from 'react';
import {Headline, Body, Button} from '@lightricks/react-design-system';
import {Divider, styled} from '@mui/material';
import {StyledCardFrame} from './common';
import useIsMobile from '@/hooks/use-is-mobile';
import Conditional from '@/components/conditional';
import {media} from '@/styles/mediaQueries';
import useGenericButtonPressedEvent from '@/hooks/analytics/use-generic-button-pressed-event';
import {screenNames} from '@/lib/delta/deltaConstants';

interface IncreaseEngagementBannerProps {
  title: string;
  subtitle: string;
  buttonText: string;
  onButtonClick: () => void;
}

const StyledEngagementBannerContainer = styled('div')(() => ({
  height: '100%',
  padding: '56px 29px 28px',
  textAlign: 'center',
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  [media.mobile]: {
    padding: '13px',
  },
}));

const StyledContentSection = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textAlign: 'center',
  width: '100%',
  flex: 1,
}));

const StyledPromptTitle = styled(Headline)(() => ({
  color: '#333',
  textAlign: 'center',
  lineHeight: 1.2,
  [media.mobile]: {
    fontSize: '20px',
    fontWeight: '700',
    marginTop: '7px',
    marginBottom: '15px',
  },
}));

const StyledDivider = styled(Divider)(() => ({
  width: '60px',
  height: '5px',
  backgroundColor: '#000',
  margin: '40px 0',
}));

const StyledPromptDescription = styled(Body)(() => ({
  marginBottom: '0',
  lineHeight: 1.4,
  textAlign: 'center',
  flex: 1,
  display: 'flex',
  alignItems: 'flex-start',
  [media.mobile]: {
    fontSize: '14px',
    fontWeight: '400',
    marginBottom: '15px',
    maxWidth: '80%',
  },
}));

const StyledConnectButton = styled(Button)(() => ({
  width: '100%',
  borderRadius: '5px',
}));

function IncreaseEngagementBanner({
  title,
  subtitle,
  buttonText,
  onButtonClick,
}: IncreaseEngagementBannerProps) {
  const isMobile = useIsMobile();
  const {pressed} = useGenericButtonPressedEvent();

  const handleButtonClick = () => {
    onButtonClick();
    pressed({
      button_name: buttonText,
      screen_name: screenNames.suggested_gigs.suggested_gigs,
    });
  };

  return (
    <StyledCardFrame>
      <StyledEngagementBannerContainer>
        <StyledContentSection>
          <StyledPromptTitle size="xl">{title}</StyledPromptTitle>

          <Conditional condition={!isMobile}>
            <StyledDivider />
          </Conditional>

          <StyledPromptDescription size="xl">
            {subtitle}
          </StyledPromptDescription>
        </StyledContentSection>

        <StyledConnectButton
          onClick={handleButtonClick}
          appearance="brand"
          mode="filled"
          size="large"
        >
          {buttonText}
        </StyledConnectButton>
      </StyledEngagementBannerContainer>
    </StyledCardFrame>
  );
}

export default IncreaseEngagementBanner;
